// favicon-badge-with-base.js
let originalIcons = null;
let dynamicIconUrls = [];

const snapshotOriginalFavicons = () => {
    if (originalIcons) return;
    originalIcons = [];
    document.querySelectorAll('link[rel*="icon"]').forEach(link => {
        originalIcons.push({
            rel: link.getAttribute('rel') || 'icon',
            href: link.getAttribute('href') || '',
            type: link.getAttribute('type') || '',
            sizes: link.getAttribute('sizes') || ''
        });
    });
};

const clearIconLinks = () => {
    document.querySelectorAll('link[rel*="icon"]').forEach(el => el.parentNode.removeChild(el));
};

const restoreFavicons = () => {
    clearIconLinks();
    if (originalIcons && originalIcons.length) {
        originalIcons.forEach(info => {
            const l = document.createElement('link');
            l.setAttribute('rel', info.rel);
            if (info.type) l.setAttribute('type', info.type);
            if (info.sizes) l.setAttribute('sizes', info.sizes);
            l.setAttribute('href', info.href);
            document.head.appendChild(l);
        });
    }
    dynamicIconUrls.forEach(url => URL.revokeObjectURL(url));
    dynamicIconUrls = [];
};

const parseSizes = s => {
    // '16x16 32x32' → [16,32]; 'any' → []
    if (!s) return [];
    if (/\bany\b/i.test(s)) return [];
    return s.split(/\s+/).map(x => parseInt(x.split('x')[0], 10)).filter(Boolean);
};

const pickBestBaseHref = (targetSize) => {
    if (!originalIcons || !originalIcons.length) return '';
    // Prefer exact size, else nearest larger, else nearest smaller, else first.
    const flat = originalIcons.map(o => ({ href: o.href, sizes: parseSizes(o.sizes) }));
    let exact = flat.find(f => f.sizes.includes(targetSize));
    if (exact) return exact.href;
    let larger = flat
        .map(f => ({ href: f.href, min: Math.min(...f.sizes.filter(n => n >= targetSize)) || Infinity }))
        .sort((a, b) => a.min - b.min)[0];
    if (larger && isFinite(larger.min)) return larger.href;
    let smaller = flat
        .map(f => ({ href: f.href, max: Math.max(...f.sizes.filter(n => n <= targetSize)) || -Infinity }))
        .sort((a, b) => b.max - a.max)[0];
    if (smaller && isFinite(smaller.max)) return smaller.href;
    return originalIcons[0].href || '';
};

const loadImageSafely = async (href) => {
    if (!href) return null;
    try {
        // Fetch → blob → object URL avoids canvas taint (if CORS allows).
        const res = await fetch(href, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
        if (!res.ok) throw new Error('fetch failed');
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        try {
            const img = await new Promise((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = reject;
                i.src = objUrl;
            });
            // Keep the blob URL alive only for this draw; caller will revoke.
            return { img, objUrl };
        } catch (e) {
            URL.revokeObjectURL(objUrl);
            return null;
        }
    } catch {
        // Fallback try: anonymous image load (may still taint and fail toBlob later)
        return await new Promise(resolve => {
            const i = new Image();
            i.crossOrigin = 'anonymous';
            i.onload = () => resolve({ img: i, objUrl: null });
            i.onerror = () => resolve(null);
            i.src = href;
        });
    }
};

const drawBadge = (ctx, size, count, badgeColor, textColor) => {
    const r = Math.round(size * 0.38);
    const cx = size - r + Math.round(size * 0.06);
    const cy = r - Math.round(size * 0.06);

    ctx.fillStyle = badgeColor;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    const text = count > 99 ? '99+' : String(count);
    ctx.fillStyle = textColor;
    const scale = text.length >= 3 ? 0.44 : 0.52;
    ctx.font = `bold ${Math.round(size * scale)}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cx, cy + 0.5);
};

const canvasToBlobUrl = (canvas) => new Promise(resolve => {
    canvas.toBlob(blob => resolve(URL.createObjectURL(blob)), 'image/png');
});

export const setFaviconBadgeFirefox = async (count, opts = {}) => {
    snapshotOriginalFavicons();

    const n = Number(count) || 0;
    if (n <= 0) {
        restoreFavicons();
        return;
    }

    const {
        sizes = [16, 32, 48, 64],
        badgeColor = '#dc3545',
        textColor = '#fff'
    } = opts;

    // Remove previous dynamic icons
    clearIconLinks();
    dynamicIconUrls.forEach(url => URL.revokeObjectURL(url));
    dynamicIconUrls = [];

    for (const size of sizes) {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // 1) Draw original favicon (best candidate) as background
        const baseHref = pickBestBaseHref(size);
        let tempUrl = null;
        if (baseHref) {
            const loaded = await loadImageSafely(baseHref);
            if (loaded && loaded.img) {
                try {
                    ctx.drawImage(loaded.img, 0, 0, size, size);
                } finally {
                    if (loaded.objUrl) URL.revokeObjectURL(loaded.objUrl);
                }
            }
        }

        // 2) Overlay badge
        drawBadge(ctx, size, n, badgeColor, textColor);

        // 3) Set as favicon (Blob URL, with sizes attr)
        const href = await canvasToBlobUrl(canvas);
        dynamicIconUrls.push(href);

        const link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        link.setAttribute('type', 'image/png');
        link.setAttribute('sizes', `${size}x${size}`);
        link.setAttribute('href', href);
        document.head.appendChild(link);
    }
};
